import BottomSheet from '@components/BottomSheet'
import Button from '@components/Button'
import Prompt from '@components/Prompt'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { useBackAfterSheet } from '@hooks/useBackAfterSheet'
import { EarnDepositReview } from '@modules/Earn/Components/EarnDepositReview'
import { InfoRow } from '@modules/Earn/Components/shared'
import { TokenCard } from '@modules/Swap/ExchangeView'
import { useEarnDeposit } from '@plug-core/hooks/earn/useEarnDeposit'
import useBalances from '@plug-core/hooks/useBalances'
import { EarnVault } from '@plug-core/interfaces/earn'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { titleize } from '@plug-core/utils/titleize'
import { ListGroup, Text, Button as UIButton } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

const EarnDepositContent = ({ vault }: { vault: EarnVault }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { tokensToBalances } = useBalances()
  const [reviewVisible, setReviewVisible] = useState(false)
  const [errorPromptVisible, setErrorPromptVisible] = useState(false)

  const {
    amount,
    setAmount,
    token,
    maxAmount,
    yearlyEarnings,
    monthlyEarnings,
    quote,
    quoteLoading,
    error,
    depositState,
    executeDeposit,
  } = useEarnDeposit(vault)

  const backAfterSheet = useBackAfterSheet(ROUTES.EARN)

  useEffect(() => {
    if (depositState === 'error') {
      setReviewVisible(false)
      setErrorPromptVisible(true)
    } else if (depositState === 'success') {
      setReviewVisible(false)
      backAfterSheet(t('earn.deposit.completed'))
    }
  }, [depositState])

  const handleReviewClose = () => setReviewVisible(false)

  return (
    <div className="flex flex-col gap-4">
      <Titlebar onActionClick={() => navigate(-1)} title={t('earn.deposit.title')} />

      <TokenCard
        token={token}
        amount={amount}
        setAmount={setAmount}
        direction="from"
        showBalance
        disableTokenPicker
        maxAmount={maxAmount}
        tokensToBalances={tokensToBalances}
      />

      <ListGroup>
        <InfoRow label={t('earn.deposit.vault')} value={vault.name} />
        <InfoRow label={t('earn.protocol')} value={titleize(vault.protocol.name)} />
        <InfoRow
          label={t('earn.apy')}
          value={formatNumber(vault.analytics.apy.total, '%', { maximumFractionDigits: 2 })}
        />

        {quote && (
          <InfoRow
            label={t('earn.deposit.gasFee')}
            value={formatMoney(Number(quote.estimate.gasCosts?.[0].amountUSD))}
          />
        )}

        <InfoRow
          label={t('earn.deposit.projectedMonthly')}
          value={
            <Text as="span" variant="body-s" weight="medium" tone="success" tabular>
              {formatMoney(monthlyEarnings)}
            </Text>
          }
        />
        <InfoRow
          label={t('earn.deposit.projectedYearly')}
          value={
            <Text as="span" variant="body-s" weight="medium" tone="success" tabular>
              {formatMoney(yearlyEarnings)}
            </Text>
          }
        />
      </ListGroup>

      <Button
        variant="rainbow"
        fullWidth
        disabled={!!error || quoteLoading || depositState === 'loading'}
        loading={quoteLoading || depositState === 'loading'}
        onClick={() => setReviewVisible(true)}
      >
        {error || t('earn.deposit.title')}
      </Button>

      {/* Kept mounted so it can slide out. */}
      <BottomSheet
        visible={reviewVisible && !!quote}
        onBackdropClick={() => depositState !== 'loading' && handleReviewClose()}
      >
        {quote && (
          <EarnDepositReview
            vault={vault}
            quote={quote}
            depositState={depositState}
            onConfirm={executeDeposit}
            onClose={handleReviewClose}
          />
        )}
      </BottomSheet>

      <Prompt show={errorPromptVisible} close={() => setErrorPromptVisible(false)}>
        <Text align="center" tone="fg-2">
          {t('earn.deposit.depositFailed')}
        </Text>
        <UIButton variant="secondary" onClick={() => setErrorPromptVisible(false)}>
          {t('common.understood')}
        </UIButton>
      </Prompt>
    </div>
  )
}

export const EarnDeposit = () => {
  const navigate = useNavigate()
  const { state } = useLocation() as { state: { vault: EarnVault } | null }

  useEffect(() => {
    if (!state?.vault) {
      navigate(-1)
    }
  }, [])

  if (!state?.vault) {
    return null
  }

  return (
    <div className="mb-3 flex flex-col">
      <EarnDepositContent vault={state.vault} />
    </div>
  )
}
